import styles from './MultipleSKU.module.scss';
import { uploadImages } from '../../../../services/Api';

const MultipleSKU = ({
    skus,
    setSkus,
    skuAttributes,
    errors,
    previewImages, // Keep for backward compatibility with ProductAdd
    showPopup,
    isEdit = false // Add this prop to distinguish between add and edit modes
}) => {
    // Find sale properties (size, color_family, etc.)
    const salePropAttributes = skuAttributes.filter(attr =>
        attr.is_sale_prop === 1
    );

    const hasSaleProps = salePropAttributes.length > 0;

    const addSku = () => {
        const newSku = {
            SellerSku: `SKU${Date.now()}`,
            quantity: "1",
            price: "",
            package_height: "",
            package_length: "",
            package_width: "",
            package_weight: "",
            Images: {
                Image: []
            },
            saleProp: salePropAttributes.reduce((acc, attr) => {
                acc[attr.name] = "";
                return acc;
            }, {}),
            package_content: "Single Item",
            tax_class: "default",
            Status: "active"
        };
        setSkus(prev => [...prev, newSku]);
    };

    const removeSku = (index) => {
        setSkus(prev => prev.filter((_, i) => i !== index));
    };

    const updateSku = (index, field, value) => {
        setSkus(prev => prev.map((sku, i) =>
            i === index ? { ...sku, [field]: value } : sku
        ));
    };

    const updateSaleProp = (index, attributeName, value) => {
        setSkus(prev => prev.map((sku, i) =>
            i === index ? {
                ...sku,
                saleProp: {
                    ...sku.saleProp,
                    [attributeName]: value
                }
            } : sku
        ));
    };

    const removeImageFromSku = (index, imageUrl) => {
        setSkus(prev => prev.map((sku, i) =>
            i === index ? {
                ...sku,
                Images: {
                    Image: sku.Images.Image.filter(img => img !== imageUrl)
                }
            } : sku
        ));
    };

    // Handle number input with validation and scroll prevention
    const handleNumberInput = (index, field, value) => {
        // Remove any non-numeric characters except decimal point
        const numericValue = value.replace(/[^\d.]/g, '');
        
        // Ensure only one decimal point
        const parts = numericValue.split('.');
        const formattedValue = parts.length > 2 
            ? parts[0] + '.' + parts.slice(1).join('')
            : numericValue;
        
        updateSku(index, field, formattedValue);
    };

    // Handle image selection from preview images (for ProductAdd)
    const handleImageSelect = (index, imageUrl) => {
        const currentSku = skus[index];
        const currentImageCount = currentSku.Images?.Image?.length || 0;

        if (currentImageCount >= 5) {
            showPopup('error', 'Error', 'Maximum 5 images allowed per SKU');
            return;
        }

        setSkus(prev => prev.map((sku, i) =>
            i === index ? {
                ...sku,
                Images: {
                    Image: [...new Set([...sku.Images.Image, imageUrl])]
                }
            } : sku
        ));
    };

    const handleSkuImageUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        const maxSizeInBytes = 2048 * 1024;
        const currentSku = skus[index];
        const currentImageCount = currentSku.Images?.Image?.length || 0;
        const currentImageUrls = currentSku.Images?.Image || [];

        // Check if total images exceed limit
        if (currentImageCount + files.length > 5) {
            showPopup('error', 'Error', 'Maximum 5 images allowed per SKU');
            event.target.value = "";
            return;
        }

        // Create a Set to track files being processed in this batch to detect duplicates within the same upload
        const filesInBatch = new Set();
        const duplicateFiles = [];

        // First pass: Check for duplicates within the same upload batch
        for (const file of files) {
            const fileIdentifier = `${file.name}-${file.size}-${file.lastModified}`;
            if (filesInBatch.has(fileIdentifier)) {
                duplicateFiles.push(file.name);
            } else {
                filesInBatch.add(fileIdentifier);
            }
        }

        // If duplicates found in the same batch, show error and return
        if (duplicateFiles.length > 0) {
            showPopup('error', 'Duplicate Images', `Same images cannot be uploaded again: ${duplicateFiles.join(', ')}`);
            event.target.value = "";
            return;
        }

        // Process each file for upload
        for (const file of files) {
            // Check file size
            if (file.size > maxSizeInBytes) {
                showPopup('error', 'Error', `${file.name} exceeds the maximum size of 2 MB`);
                continue;
            }

            try {
                const formDataImage = new FormData();
                formDataImage.append('image', file);
                const response = await uploadImages(formDataImage);

                // ✅ Check if API responded with any internal error message
                if (
                    response.data &&
                    response.data.code &&
                    response.data.code === "302" &&
                    response.data.detail &&
                    response.data.detail.length > 0
                ) {
                    // Extract the detailed message
                    const detailMsg = response.data.detail[0]?.message || 'Unknown image upload error';
                    showPopup('error', 'Error', detailMsg);
                    continue; // Skip further processing
                }

                // ✅ Handle 422 status with dimension errors
                if (response.status === 422 && response.data?.data?.detail?.length > 0) {
                    const detailMsg = response.data.data.detail[0]?.message || 'Image dimension too small';
                    showPopup('error', 'Error', detailMsg);
                    continue;
                }

                if (response.status === 200 && response.data?.data?.image?.url) {
                    const imageUrl = response.data.data.image.url;

                    // Check if the uploaded image URL already exists in current SKU images
                    if (currentImageUrls.includes(imageUrl)) {
                        showPopup('error', 'Duplicate Image', `Same image cannot be uploaded again: ${file.name}`);
                        continue;
                    }

                    setSkus(prev => prev.map((sku, i) =>
                        i === index ? {
                            ...sku,
                            Images: {
                                Image: [...new Set([...sku.Images.Image, imageUrl])]
                            }
                        } : sku
                    ));

                    showPopup('success', 'Success', `${file.name} uploaded successfully`);
                } else if (response.data?.message) {
                    showPopup('error', 'Error', response.data.message);
                } else {
                    showPopup('error', 'Error', 'Failed to upload image');
                }

            } catch (error) {
                console.error('Image upload failed:', error);

                // ✅ Handle error response with proper structure
                if (error.response?.data?.data?.detail?.length > 0) {
                    const detailMsg = error.response.data.data.detail[0]?.message || 'Image upload failed';
                    showPopup('error', 'Error', detailMsg);
                } else if (error.response?.data?.message) {
                    showPopup('error', 'Error', error.response.data.message);
                } else {
                    showPopup('error', 'Error', 'Failed to upload image');
                }
            }
        }

        event.target.value = "";
    };

    const renderSalePropField = (sku, index, attribute) => {
        const value = sku.saleProp[attribute.name] || '';

        const formatValue = (val) => {
            if (!val) return '';
            const lowerVal = val.toLowerCase();

            // Handle only these two specific values
            if (lowerVal === 'int:one size') {
                return 'Int:one size';
            }
            if (lowerVal === 'multicolor') {
                return 'Multicolor';
            }

            return val;
        };

        const displayValue = formatValue(value);

        if (attribute.input_type === 'enumInput' && attribute.options) {
            return (
                <select
                    value={displayValue}
                    onChange={(e) => updateSaleProp(index, attribute.name, e.target.value)}
                    className={`${styles.select_field}`}
                >
                    <option value="">Select {attribute.label}</option>
                    {attribute.options.map(option => {
                        const optionValue = formatValue(option.name || option.en_name || '');
                        return (
                            <option key={option.id} value={optionValue}>
                                {optionValue}
                            </option>
                        );
                    })}
                </select>
            );
        }

        return (
            <input
                type="text"
                value={displayValue}
                onChange={(e) => updateSaleProp(index, attribute.name, e.target.value)}
                placeholder={`Enter ${attribute.label}`}
                className={`${styles.input_field}`}
            />
        );
    };

    const renderSkuField = (sku, index, field, label, type = "text") => {
        const value = sku[field] || '';
        const errorKey = `skus.${index}.${field}`;
        const hasError = errors[errorKey];

        return (
            <div className={`${styles.field} ${hasError ? styles.error : ''}`}>
                <label>{label}</label>
                <input
                        type="text"
                        inputMode="decimal"
                        value={value}
                        onChange={(e) => handleNumberInput(index, field, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        placeholder={`Enter ${label}`}
                        className={`${styles.input_field}`}
                    />
                {hasError && <span className={`${styles.error_message}`}>{hasError}</span>}
            </div>
        );
    };

    const renderSkuImageSection = (sku, index) => {
        return (
            <div className={`${styles.sku_images}`}>
                <h6>SKU Specific Images</h6>
                <div className={`${styles.images_selection}`}>
                    <p>Upload images for this variation:</p>

                    {/* File upload for SKU-specific images */}
                    <div className={`${styles.upload_section}`}>
                        <label className={`${styles.upload_btn}`}>
                            + Add Images
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleSkuImageUpload(index, e)}
                                style={{ display: 'none' }}
                            />
                        </label>
                        <span className={`${styles.upload_hint0}`}>Max 5 images per SKU, 2MB each</span>
                    </div>

                    {/* Selected Images Preview */}
                    {sku.Images.Image.length > 0 && (
                        <div className={`${styles.selected_images}`}>
                            <p>Selected images for this SKU:</p>
                            <div className={`${styles.selected_preview}`}>
                                {sku.Images.Image.map((imageUrl, imgIndex) => (
                                    <div key={imgIndex} className={`${styles.selected_thumb}`}>
                                        <img src={imageUrl} alt={`Selected ${imgIndex}`} />
                                        <button
                                            type="button"
                                            onClick={() => removeImageFromSku(index, imageUrl)}
                                            className={`${styles.remove_image_btn}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // For ProductAdd: Show image selection from previewImages
    const renderProductAddImageSection = (sku, index) => {
        return (
            <div className={`${styles.sku_images}`}>
                <h6>SKU Specific Images</h6>
                <div className={`${styles.images_selection}`}>
                    <p>Upload images for this variation:</p>

                    {/* File upload for SKU-specific images */}
                    <div className={`${styles.upload_section}`}>
                        <label className={`${styles.upload_btn}`}>
                            + Add Images
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleSkuImageUpload(index, e)}
                                style={{ display: 'none' }}
                            />
                        </label>
                        <span className={`${styles.upload_hint}`}>Max 5 images per SKU, 2MB each</span>
                    </div>

                    {/* Selected Images Preview */}
                    {sku.Images.Image.length > 0 && (
                        <div className={`${styles.selected_images}`}>
                            <p>Selected images for this SKU:</p>
                            <div className={`${styles.selected_preview}`}>
                                {sku.Images.Image.map((imageUrl, imgIndex) => (
                                    <div key={imgIndex} className={`${styles.selected_thumb}`}>
                                        <img src={imageUrl} alt={`Selected ${imgIndex}`} />
                                        <button
                                            type="button"
                                            onClick={() => removeImageFromSku(index, imageUrl)}
                                            className={`${styles.remove_image_btn}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`${styles.multiple_sku}`}>
            <div className={`${styles.section_header}`}>
                <h4>SKU Variations</h4>
                <p>Add multiple SKUs for different variations</p>
                <button type="button" onClick={addSku} className={`${styles.add_sku_btn}`}>
                    + Add SKU Variation
                </button>
            </div>

            {skus.length === 0 && (
                <div className={`${styles.no_skus}`}>
                    <p>No SKU variations added. Click the button above to add variations.</p>
                </div>
            )}

            <div className={`${styles.sku_list}`}>
                {skus.map((sku, index) => (
                    <div key={index} className={`${styles.sku_card}`}>
                        <div className={`${styles.sku_header}`}>
                            <h5>SKU Variation {index + 1}</h5>
                            <button
                                type="button"
                                onClick={() => removeSku(index)}
                                className={`${styles.remove_btn}`}
                                disabled={skus.length === 1}
                            >
                                Remove
                            </button>
                        </div>

                        <div className={`${styles.sku_content}`}>
                            {/* Sale Properties */}
                            {hasSaleProps && (
                                <div className={`${styles.sale_props}`}>
                                    <h6>Variation Properties</h6>
                                    <div className={`${styles.sale_props_grid}`}>
                                        {salePropAttributes.map(attribute => (
                                            <div key={attribute.id} className={`${styles.sale_prop_field}`}>
                                                <label>{attribute.label}</label>
                                                {renderSalePropField(sku, index, attribute)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* SKU Details */}
                            <div className={`${styles.sku_details}`}>
                                <h6>SKU Details</h6>
                                <div className={`${styles.details_grid}`}>
                                    {renderSkuField(sku, index, 'SellerSku', 'Seller SKU', 'text')}
                                    {renderSkuField(sku, index, 'quantity', 'Quantity', 'number')}
                                    {renderSkuField(sku, index, 'price', 'Price($)', 'number')}
                                    {renderSkuField(sku, index, 'package_weight', 'Weight (kg)', 'number')}
                                    {renderSkuField(sku, index, 'package_length', 'Length (cm)', 'number')}
                                    {renderSkuField(sku, index, 'package_width', 'Width (cm)', 'number')}
                                    {renderSkuField(sku, index, 'package_height', 'Height (cm)', 'number')}
                                </div>
                            </div>

                            {/* SKU Images - Different rendering based on mode */}
                            {isEdit ? (
                                // For ProductEdit: Show upload functionality
                                renderSkuImageSection(sku, index)
                            ) : (
                                // For ProductAdd: Show selection from previewImages
                                renderProductAddImageSection(sku, index)
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MultipleSKU;